import { Injectable } from '@angular/core';
import { NotificationsService, Notification } from './notifications.service';

// Interface TicketNotification removida - usando Notification diretamente

@Injectable({
  providedIn: 'root',
})
export class TicketNotificationsService {
  constructor(private notificationsService: NotificationsService) {}

  // Método para verificar se o usuário logado deve receber notificação
  shouldNotifyUser(updatedTicket: any, currentUserId: string): boolean {
    if (!updatedTicket || !currentUserId) {
      return false;
    }

    // Verificar se o usuário é o desenvolvedor
    if (updatedTicket.developer?.id === currentUserId) {
      return true;
    }

    // Verificar se o usuário é o executor
    if (updatedTicket.executor?.id === currentUserId) {
      return true;
    }

    // Verificar se o usuário é o responsável
    if (updatedTicket.responsible?.id === currentUserId) {
      return true;
    }

    // Verificar se o usuário é participante do chamado
    if (updatedTicket.participants && Array.isArray(updatedTicket.participants)) {
      const isParticipant = updatedTicket.participants.some(
        (participant: any) => participant.id === currentUserId
      );
      if (isParticipant) {
        return true;
      }
    }

    // Verificar se o usuário é o usuário que abriu o chamado
    if (updatedTicket.user_opened?.id === currentUserId) {
      return true;
    }

    // Verificar se o usuário é o usuário principal do chamado
    if (updatedTicket.user?.id === currentUserId) {
      return true;
    }

    // Verificar nos ticketUsers se o usuário está envolvido
    if (updatedTicket.ticketUsers && Array.isArray(updatedTicket.ticketUsers)) {
      const isInvolvedInTicketUsers = updatedTicket.ticketUsers.some(
        (ticketUser: any) =>
          ticketUser.user?.id === currentUserId || ticketUser.user_id === currentUserId
      );
      if (isInvolvedInTicketUsers) {
        return true;
      }
    }

    // Verificar nos details se o usuário está envolvido
    if (updatedTicket.details && Array.isArray(updatedTicket.details)) {
      const isInvolvedInDetails = updatedTicket.details.some(
        (detail: any) => detail.user?.id === currentUserId || detail.user_id === currentUserId
      );
      if (isInvolvedInDetails) {
        return true;
      }
    }

    // Verificar nas tasks/tarefas se o usuário é desenvolvedor de alguma tarefa
    if (updatedTicket.tasks && Array.isArray(updatedTicket.tasks)) {
      const isTaskDeveloper = updatedTicket.tasks.some(
        (task: any) => task.user?.id === currentUserId || task.user_id === currentUserId
      );
      if (isTaskDeveloper) {
        return true;
      }
    }

    // Verificar se o usuário é desenvolvedor de alguma tarefa nos details
    if (updatedTicket.details && Array.isArray(updatedTicket.details)) {
      const isTaskDeveloperInDetails = updatedTicket.details.some((detail: any) => {
        // Verificar se o detail tem tasks e se o usuário é desenvolvedor de alguma
        if (detail.tasks && Array.isArray(detail.tasks)) {
          return detail.tasks.some(
            (task: any) => task.user?.id === currentUserId || task.user_id === currentUserId
          );
        }
        return false;
      });
      if (isTaskDeveloperInDetails) {
        return true;
      }
    }

    return false;
  }

  // Método para criar notificação baseada no ticket atualizado
  createNotificationFromTicket(
    updatedTicket: any,
    currentUserId: string,
    description: string
  ): Omit<Notification, 'id' | 'dateTime'> | null {
    if (!this.shouldNotifyUser(updatedTicket, currentUserId)) {
      return null;
    }

    // Usar o número do ticket correto (ticket_number_teorema ou id como fallback)
    const ticketRef = updatedTicket.ticket_number_teorema
      ? updatedTicket.ticket_number_teorema.padStart(10, '0')
      : updatedTicket.id?.toString().padStart(10, '0') || 'N/A';

    let title = 'Chamado atualizado';
    let summary = description;
    let type = 'update';

    // Determinar ícone baseado no tipo de chamado
    let icon = 'bi-ticket';
    let iconColor = 'text-primary';
    let iconTooltip = 'Chamado';

    if (updatedTicket.urgent) {
      icon = 'bi-exclamation-triangle-fill';
      iconColor = 'text-danger';
      iconTooltip = 'Chamado Urgente';
    } else if (
      updatedTicket.ticket_type?.description === 'Chamada Tipo Problema' ||
      updatedTicket.ticket_type?.description === 'Chamada Defeito / Retrabalho'
    ) {
      icon = 'bi-bug-fill';
      iconColor = 'text-danger';
      iconTooltip = 'Chamado com Defeito';
    }

    return {
      ref: ticketRef, // Número do ticket formatado para exibição
      uuid: updatedTicket.id?.toString() || '', // ID real do ticket para navegação
      title,
      summary,
      type: 'ticket', // Usar o tipo correto 'ticket'
      userId: currentUserId,
      icon,
      iconColor,
      iconTooltip,
    };
  }

  // Método para adicionar notificação de chamado
  addTicketNotification(notification: Omit<Notification, 'id' | 'dateTime'>): void {
    this.notificationsService.addNotification(notification);
  }

  // Método para obter notificações de chamados
  getTicketNotifications(): Notification[] {
    return this.notificationsService.getNotifications();
  }

  // Método para obter observable de notificações
  get notifications$() {
    return this.notificationsService.notifications$;
  }

  // Método para deletar notificação
  deleteNotification(notificationId: string): void {
    this.notificationsService.deleteNotificationById(notificationId);
  }

  // Método para limpar todas as notificações
  clearAllNotifications(): void {
    this.notificationsService.clearAllNotifications();
  }

  // Método para atualizar título da página
  updatePageTitle(notificationCount: number): void {
    this.notificationsService.updatePageTitle(notificationCount);
  }
}
